import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Search, Filter, Clock, Users, Calendar, ChevronDown, Star, Activity,
  Target, BookOpen
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { cn, debounce } from '@/lib/utils'
import toast from 'react-hot-toast'

// Mock data for development (will be replaced with API)
export const mockDemos = [
  {
    id: '3',
    name: 'HPE Juniper AI-Driven Operations',
    description: 'Discover Mist AI capabilities for automated network operations, predictive insights, and user experience monitoring. See how AI transforms network management.',
    durationMinutes: 75,
    maxAttendees: 10,
    status: 'ACTIVE',
    requirements: ['Network operations experience', 'Interest in AI/ML applications'],
    objectives: [
      'Experience AI-driven network insights',
      'See automated troubleshooting',
      'Understand virtual network assistant',
      'Learn about predictive analytics'
    ],
    products: [
      { vendor: 'HPE Juniper', productName: 'Mist Edge', category: 'EDGE_COMPUTING' },
      { vendor: 'HPE Juniper', productName: 'AP63 Access Point', category: 'ACCESS_POINTS' }
    ],
    totalBookings: 32,
    imageUrl: '/demos/mist-ai.jpg',
    tags: ['AI/ML', 'Automation', 'Analytics']
  },
  {
    id: 'aruba-central',
    name: 'HPE Aruba Networking Central - AI-Native Cloud Management',
    description: 'Experience the power of AI-native, cloud-managed networking with HPE Aruba Networking Central. Get hands-on with unified management for wireless, wired, VPN, and SD-WAN infrastructure across campus, branch, and data center environments.',
    durationMinutes: 75,
    maxAttendees: 10,
    status: 'ACTIVE',
    requirements: ['Basic networking knowledge', 'Web browser', 'Understanding of network management principles'],
    objectives: [
      'Experience AI-native network management and insights',
      'Navigate the intuitive Central dashboard and solar system view',
      'Understand unified policy management across all network types',
      'Explore network time travel for historical analysis',
      'See zero trust security principles in action',
      'Learn about automated network provisioning and orchestration'
    ],
    products: [
      {
        id: '1',
        vendor: 'HPE Aruba',
        productName: 'Central Cloud Management',
        category: 'MANAGEMENT',
        description: 'AI-native, cloud-managed networking platform for unified control',
        keyFeatures: ['AI Insights', 'Unified Management', 'Zero Trust', 'Global Policy Orchestration']
      },
      {
        id: '2',
        vendor: 'HPE Aruba',
        productName: 'Access Points (Wi-Fi 6E/7)',
        category: 'ACCESS_POINTS',
        description: 'Latest wireless access points with Wi-Fi 6E and 7 capabilities',
        keyFeatures: ['Wi-Fi 7 Ready', '6GHz Support', 'AI Optimization', 'Power Efficiency']
      },
      {
        id: '3',
        vendor: 'HPE Aruba',
        productName: 'CX Switches',
        category: 'SWITCHES',
        description: 'Advanced switching portfolio with cloud-native management',
        keyFeatures: ['AOS-CX', 'Stacking', 'PoE++', 'Analytics Ready']
      }
    ],
    totalBookings: 42,
    completedBookings: 38,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-01-25T10:00:00Z',
        endTime: '2024-01-25T11:15:00Z',
        attendeeCount: 8,
        status: 'CONFIRMED'
      },
      {
        id: '2',
        startTime: '2024-01-28T14:00:00Z',
        endTime: '2024-01-28T15:15:00Z',
        attendeeCount: 6,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'Sarah Martinez',
        company: 'Global Enterprise Inc',
        role: 'Network Operations Manager',
        feedback: 'Central\'s AI insights have transformed how we manage our network. The unified view is incredible.',
        rating: 5
      },
      {
        name: 'David Kim',
        company: 'TechStart Solutions',
        role: 'IT Director',
        feedback: 'The self-paced demo was perfect for understanding Central\'s capabilities before our purchase.',
        rating: 5
      }
    ],
    imageUrl: '/demos/aruba-central.jpg',
    tags: ['Cloud Management', 'AI-Native', 'Unified Control', 'Zero Trust', 'Automation']
  },
  {
    id: 'axis-redirect',
    name: 'HPE Aruba Networking SSE',
    description: 'Experience Aruba Security Service Edge (SSE) with Zero Trust Network Access capabilities. Access the live Axis Security portal with streamlined authentication.',
    durationMinutes: 45,
    maxAttendees: 1,
    status: 'ACTIVE',
    requirements: ['Web browser', 'Basic understanding of security concepts'],
    objectives: [
      'Experience Zero Trust Network Access (ZTNA)',
      'Explore Security Service Edge (SSE) features',
      'Understand cloud-based security architecture',
      'See real-time threat protection in action'
    ],
    products: [
      {
        id: '1',
        vendor: 'Aruba',
        productName: 'SSE (Security Service Edge)',
        category: 'SECURITY',
        description: 'Comprehensive Security Service Edge solution with Zero Trust capabilities',
        keyFeatures: ['Zero Trust', 'Cloud-native', 'Identity-based Access', 'Real-time Monitoring']
      },
      {
        id: '2',
        vendor: 'Axis Security',
        productName: 'ZTNA Platform',
        category: 'SECURITY',
        description: 'Advanced Zero Trust Network Access platform for secure remote connectivity',
        keyFeatures: ['Application Access', 'Device Trust', 'User Verification', 'Policy Enforcement']
      }
    ],
    totalBookings: 8,
    completedBookings: 6,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-01-20T14:00:00Z',
        endTime: '2024-01-20T14:45:00Z',
        attendeeCount: 1,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'Mike Chen',
        company: 'SecureCorp',
        role: 'Security Architect',
        feedback: 'Excellent demonstration of Zero Trust principles in action.',
        rating: 5
      }
    ],
    imageUrl: '/demos/aruba-sse.jpg',
    tags: ['SSE', 'Zero Trust', 'Cloud Security', 'ZTNA']
  },
  {
    id: 'edgeconnect-sdwan',
    name: 'HPE Aruba EdgeConnect SD-WAN - Business Intent Networking',
    description: 'Experience the power of EdgeConnect SD-WAN with HPE Aruba business intent overlays. Discover unified orchestration, dynamic path control, and tunnel bonding for optimized branch connectivity and cloud access.',
    durationMinutes: 90,
    maxAttendees: 8,
    status: 'ACTIVE',
    requirements: ['Basic WAN/networking knowledge', 'Understanding of SD-WAN concepts', 'Web browser'],
    objectives: [
      'Experience business intent overlays and application-specific routing',
      'Understand unified orchestration across branch locations',
      'See dynamic path control and tunnel bonding in action',
      'Explore integrated next-generation firewall and security features',
      'Learn about zero-touch provisioning and cloud integration',
      'Discover real-time analytics and network performance monitoring'
    ],
    products: [
      {
        id: '1',
        vendor: 'HPE Aruba',
        productName: 'EdgeConnect SD-WAN Platform',
        category: 'SD_WAN',
        description: 'Advanced SD-WAN solution with business intent overlays for optimized branch connectivity',
        keyFeatures: ['Business Intent Overlays', 'Unified Orchestration', 'Dynamic Path Control', 'Integrated Security']
      },
      {
        id: '2',
        vendor: 'HPE Aruba',
        productName: 'Orchestrator Platform',
        category: 'MANAGEMENT',
        description: 'Centralized management and orchestration for SD-WAN deployments',
        keyFeatures: ['Zero-Touch Provisioning', 'Global Policy Management', 'Real-time Analytics', 'Multi-tenant Support']
      },
      {
        id: '3',
        vendor: 'HPE Aruba',
        productName: 'EdgeConnect Appliances',
        category: 'EDGE_COMPUTING',
        description: 'High-performance edge appliances for branch and cloud connectivity',
        keyFeatures: ['Application Acceleration', 'WAN Optimization', 'Integrated Security', 'Cloud Integration']
      }
    ],
    totalBookings: 28,
    completedBookings: 24,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-01-26T11:00:00Z',
        endTime: '2024-01-26T12:30:00Z',
        attendeeCount: 6,
        status: 'CONFIRMED'
      },
      {
        id: '2',
        startTime: '2024-01-30T15:00:00Z',
        endTime: '2024-01-30T16:30:00Z',
        attendeeCount: 4,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'Jennifer Wilson',
        company: 'MultiSite Corp',
        role: 'Network Engineering Manager',
        feedback: 'EdgeConnect\'s AI optimization has dramatically improved our branch performance and reduced costs.',
        rating: 5
      },
      {
        name: 'Robert Zhang',
        company: 'Global Retail Solutions',
        role: 'Infrastructure Director',
        feedback: 'The test drive experience gave us confidence in EdgeConnect\'s capabilities before deployment.',
        rating: 5
      }
    ],
    imageUrl: '/demos/edgeconnect-sdwan.jpg',
    tags: ['SD-WAN', 'Business Intent', 'WAN Optimization', 'Branch Connectivity', 'Path Conditioning']
  },
  {
    id: 'clearpass-nac',
    name: 'HPE Aruba ClearPass Policy Manager - Zero Trust NAC',
    description: 'Experience comprehensive Network Access Control (NAC) with HPE Aruba ClearPass Policy Manager. Discover role-based access enforcement, device profiling, guest management, and Zero Trust security across multivendor wired, wireless, and VPN infrastructures.',
    durationMinutes: 60,
    maxAttendees: 12,
    status: 'ACTIVE',
    requirements: ['Basic networking knowledge', 'Understanding of security concepts', 'Familiarity with AAA principles'],
    objectives: [
      'Experience role-based network access control across multivendor infrastructure',
      'Understand device profiling and posture assessment capabilities',
      'Explore guest management and self-service onboarding features',
      'See Zero Trust security principles in action with policy enforcement',
      'Learn about integration with HPE Aruba 360 Security Exchange',
      'Discover automated threat detection and response workflows'
    ],
    products: [
      {
        id: '1',
        vendor: 'HPE Aruba',
        productName: 'ClearPass Policy Manager',
        category: 'SECURITY',
        description: 'Comprehensive NAC solution with role-based access control and policy management',
        keyFeatures: ['Role-based Access Control', 'Device Profiling', 'Guest Management', 'Zero Trust Enforcement']
      },
      {
        id: '2',
        vendor: 'HPE Aruba',
        productName: 'ClearPass Device Insight',
        category: 'ANALYTICS',
        description: 'Next-generation device profiling with machine learning and deep packet inspection',
        keyFeatures: ['ML-based Profiling', 'DPI Support', 'Advanced Reporting', 'Real-time Analytics']
      },
      {
        id: '3',
        vendor: 'HPE Aruba',
        productName: '360 Security Exchange',
        category: 'SECURITY',
        description: 'Integrated security ecosystem with automated threat detection and response',
        keyFeatures: ['Threat Intelligence', 'Automated Response', 'Third-party Integration', 'Policy Orchestration']
      }
    ],
    totalBookings: 35,
    completedBookings: 31,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-01-27T13:00:00Z',
        endTime: '2024-01-27T14:00:00Z',
        attendeeCount: 10,
        status: 'CONFIRMED'
      },
      {
        id: '2',
        startTime: '2024-01-31T10:00:00Z',
        endTime: '2024-01-31T11:00:00Z',
        attendeeCount: 8,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'Marcus Thompson',
        company: 'Enterprise Security Corp',
        role: 'CISO',
        feedback: 'ClearPass transformed our network security posture. The Zero Trust implementation was seamless.',
        rating: 5
      },
      {
        name: 'Lisa Rodriguez',
        company: 'Global Manufacturing Inc',
        role: 'Network Security Manager',
        feedback: 'Device profiling and automated policy enforcement have significantly reduced our security incidents.',
        rating: 5
      }
    ],
    imageUrl: '/demos/clearpass-nac.jpg',
    tags: ['NAC', 'Zero Trust', 'Policy Management', 'Device Profiling', 'Guest Access', 'Security']
  },
  {
    id: 'uxi-demo',
    name: 'HPE Aruba Networking User Experience Insight (UXI)',
    description: 'Experience proactive network assurance with HPE Aruba Networking UXI - a digital experience monitoring solution that validates network health and application performance. Monitor network and application performance from a user perspective with AI-powered analytics and automated synthetic testing.',
    durationMinutes: 45,
    maxAttendees: 15,
    status: 'ACTIVE',
    requirements: ['Basic networking knowledge', 'Understanding of network monitoring concepts', 'Web browser access'],
    objectives: [
      'Experience AI-powered digital experience monitoring (DEM)',
      'Explore synthetic testing for applications like Office 365, Netflix, and Skype',
      'Understand proactive network assurance and incident detection',
      'See real-time network performance analytics and insights',
      'Learn about multivendor support and remote employee monitoring',
      'Discover automated troubleshooting with dynamic packet captures'
    ],
    products: [
      {
        id: '1',
        vendor: 'HPE Aruba',
        productName: 'UXI G6E Sensors',
        category: 'MONITORING',
        description: 'Wi-Fi 6E sensors with dual radios for comprehensive network monitoring',
        keyFeatures: ['Wi-Fi 6E Support', 'Dual Radio', 'AI Analytics', 'Synthetic Testing']
      },
      {
        id: '2',
        vendor: 'HPE Aruba',
        productName: 'UXI Agent for Windows/Mac/Android',
        category: 'MONITORING',
        description: 'Software agents for end-user device monitoring and performance analysis',
        keyFeatures: ['Real-time Monitoring', 'Performance Metrics', 'Self-service Portal', 'Multi-platform Support']
      },
      {
        id: '3',
        vendor: 'HPE Aruba',
        productName: 'UXI Cloud Dashboard',
        category: 'MANAGEMENT',
        description: 'Intuitive cloud-based dashboard with AI-powered insights and alerts',
        keyFeatures: ['AI Insights', 'Real-time Alerts', 'Historical Analytics', 'Third-party Integrations']
      }
    ],
    totalBookings: 28,
    completedBookings: 24,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-02-01T09:00:00Z',
        endTime: '2024-02-01T09:45:00Z',
        attendeeCount: 8,
        status: 'CONFIRMED'
      },
      {
        id: '2',
        startTime: '2024-02-05T15:00:00Z',
        endTime: '2024-02-05T15:45:00Z',
        attendeeCount: 12,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'Michael Chen',
        company: 'Regional Healthcare Network',
        role: 'Network Operations Director',
        feedback: 'UXI has transformed our network monitoring approach. We can now proactively identify and resolve issues before they impact patient care.',
        rating: 5
      },
      {
        name: 'Jennifer Rodriguez',
        company: 'Education District',
        role: 'IT Manager',
        feedback: 'The synthetic testing capabilities help us ensure consistent application performance across all our school locations.',
        rating: 5
      }
    ],
    imageUrl: '/demos/uxi-monitoring.jpg',
    tags: ['Digital Experience', 'AI Monitoring', 'Network Assurance', 'Synthetic Testing', 'Performance Analytics', 'Proactive Monitoring']
  },
  {
    id: 'vision-dashboards',
    name: 'Vision Dashboards - Healthy Spaces Monitoring',
    description: 'Westcoast partners can offer Healthy Spaces - an end-to-end IoT solution leveraging HPE Aruba Networks wireless infrastructure. Deploy sensors that seamlessly integrate with intelligent infrastructure to monitor temperature, CO2 levels, humidity, space occupancy, and leak detection, delivering actionable insights for staff, students and visitors.',
    durationMinutes: 45,
    maxAttendees: 12,
    status: 'ACTIVE',
    requirements: ['Basic facility management knowledge', 'Understanding of environmental monitoring concepts', 'Web browser access'],
    objectives: [
      'Experience at-a-glance environmental dashboards',
      'Monitor temperature, CO2, humidity, and leak detection',
      'Configure custom thresholds and alerting',
      'Analyze historical trends for compliance and optimization',
      'Understand space occupancy monitoring',
      'Learn about sensor deployment and integration'
    ],
    products: [
      {
        id: '1',
        vendor: 'Vision',
        productName: 'Multi-Sensors',
        category: 'IOT',
        description: 'Small environmental sensors for comprehensive space monitoring',
        keyFeatures: ['Temperature Monitoring', 'CO2 Detection', 'Humidity Tracking', 'Occupancy Sensing']
      },
      {
        id: '2',
        vendor: 'Vision',
        productName: 'Vision Dashboard Platform',
        category: 'MANAGEMENT',
        description: 'Cloud-based dashboard for environmental monitoring and analytics',
        keyFeatures: ['Real-time Monitoring', 'Custom Alerts', 'Historical Analytics', 'Compliance Reporting']
      },
      {
        id: '3',
        vendor: 'Vision',
        productName: 'Leak Detection Sensors',
        category: 'IOT',
        description: 'Advanced sensors for early leak detection and prevention',
        keyFeatures: ['Early Detection', 'Instant Alerts', 'Damage Prevention', 'Easy Integration']
      }
    ],
    totalBookings: 18,
    completedBookings: 15,
    upcomingBookings: [
      {
        id: '1',
        startTime: '2024-02-08T10:00:00Z',
        endTime: '2024-02-08T10:45:00Z',
        attendeeCount: 6,
        status: 'CONFIRMED'
      },
      {
        id: '2',
        startTime: '2024-02-12T14:00:00Z',
        endTime: '2024-02-12T14:45:00Z',
        attendeeCount: 8,
        status: 'CONFIRMED'
      }
    ],
    testimonials: [
      {
        name: 'David Williams',
        company: 'University Campus Facilities',
        role: 'Facilities Director',
        feedback: 'Vision Dashboards has transformed how we monitor and manage our campus environment. The early leak detection alone has saved us thousands in potential damage.',
        rating: 5
      },
      {
        name: 'Emma Thompson',
        company: 'Corporate Office Management',
        role: 'Sustainability Manager',
        feedback: 'The CO2 and temperature monitoring helps us optimize our HVAC systems and create healthier workspaces for our employees.',
        rating: 5
      }
    ],
    imageUrl: '/demos/vision-dashboards.jpg',
    tags: ['Environmental Monitoring', 'IoT', 'Healthy Spaces', 'Facility Management', 'Air Quality', 'Smart Buildings']
  }
]

const statusColors = {
  ACTIVE: 'from-green-500 to-emerald-500',
  INACTIVE: 'from-gray-500 to-gray-600',
  MAINTENANCE: 'from-orange-500 to-yellow-500'
}

export default function DemoList() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [isLoading] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      // This would trigger API call
      console.log('Searching for:', term)
    }, 300),
    []
  )

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm)
    }
  }, [searchTerm, debouncedSearch])

  // Filter and sort demos
  const filteredDemos = useMemo(() => {
    let filtered = mockDemos

    if (searchTerm) {
      filtered = filtered.filter(demo => 
        demo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        demo.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(demo => demo.status === selectedStatus)
    }

    // Sort demos
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'duration':
          return a.durationMinutes - b.durationMinutes
        case 'popularity':
          return b.totalBookings - a.totalBookings
        default:
          return 0
      }
    })

    return filtered
  }, [searchTerm, selectedStatus, sortBy])

  const handleDemoClick = (demoId: string) => {
    navigate(`/demos/${demoId}`)
  }

  const handleBookDemo = (e: React.MouseEvent, demoId: string) => {
    e.stopPropagation()
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: t.visible ? 1 : 0, scale: t.visible ? 1 : 0.8, y: t.visible ? 0 : 50 }}
        exit={{ opacity: 0, scale: 0.8, y: 50 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 p-1 shadow-2xl max-w-md"
      >
        <div className="relative overflow-hidden rounded-xl bg-white dark:bg-gray-900 p-5">
          <div className="flex items-start gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 shadow-lg shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 dark:text-white text-base mb-1">
                Registration Form Opening! 📋
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                This form is currently in preview mode. Registration functionality will be available soon!
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    ), {
      duration: 3500,
      position: 'bottom-center',
    })
    navigate(`/booking/new/${demoId}`)
  }

  return (
    <div className="min-h-screen animated-gradient-bg relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-20 left-0 w-96 h-96 bg-blue-500/10 dark:bg-blue-400/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/10 dark:bg-purple-400/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 dark:bg-indigo-400/5 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10">
        {/* Hero Section */}
        <section className="pt-20 pb-12">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-blue-600 bg-clip-text text-transparent">
                  Demo Catalogue
                </span>
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
                Explore our comprehensive collection of interactive demos showcasing
                HPE Aruba and HPE Juniper technologies
              </p>

              {/* Branded Images Section */}
              <div className="mb-12">
                <div className="container mx-auto px-4">
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Image 4 */}
                  <div className="lg:col-span-1">
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="relative group h-full"
                    >
                      {/* Decorative background elements */}
                      <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 via-[#BBD134]/20 to-purple-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                      {/* Main image container */}
                      <div className="relative glass-morphism-dark rounded-2xl overflow-hidden border-2 border-[#BBD134]/30 hover:border-[#BBD134]/60 transition-all duration-500 shadow-2xl h-full min-h-[350px]">
                        {/* Accent bar with brand color */}
                        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-[#BBD134] to-purple-500 z-10" />

                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className="relative h-full"
                        >
                          <img
                            src="/images/branded/4.png"
                            alt="Interactive Demo Experience"
                            className="w-full h-full object-cover"
                          />

                          {/* Overlay gradient on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          {/* Interactive corner accents */}
                          <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-[#BBD134] opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-[#BBD134] opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                        </motion.div>

                        {/* Caption bar */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80 backdrop-blur-md p-4 lg:p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-10">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg lg:text-xl font-bold text-white mb-1">
                                Interactive Demos
                              </h3>
                              <p className="text-xs lg:text-sm text-gray-300">
                                Hands-on experience with real equipment
                              </p>
                            </div>
                            <motion.div
                              whileHover={{ rotate: 360 }}
                              transition={{ duration: 0.6 }}
                              className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-[#BBD134] to-blue-500 rounded-full flex items-center justify-center flex-shrink-0"
                            >
                              <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                            </motion.div>
                          </div>
                        </div>
                      </div>

                      {/* Floating stat badges */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="absolute -left-2 lg:-left-4 top-1/3 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-blue-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                          Real-Time
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Live Labs</div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                        className="absolute -right-2 lg:-right-4 bottom-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-[#BBD134]/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#BBD134] to-green-600 bg-clip-text text-transparent">
                          Hands-On
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Interactive</div>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Image 5 */}
                  <div className="lg:col-span-2">
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="relative group h-full"
                    >
                      {/* Decorative background elements */}
                      <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 via-orange-500/20 to-pink-500/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                      {/* Main image container */}
                      <div className="relative glass-morphism-dark rounded-2xl overflow-hidden border-2 border-purple-400/30 hover:border-purple-400/60 transition-all duration-500 shadow-2xl h-full min-h-[350px]">
                        {/* Accent bar with brand color */}
                        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-500 via-orange-500 to-pink-500 z-10" />

                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className="relative h-full"
                        >
                          <img
                            src="/images/branded/5.png"
                            alt="Expert-Led Demonstrations"
                            className="w-full h-full object-cover"
                          />

                          {/* Overlay gradient on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          {/* Interactive corner accents */}
                          <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-purple-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-purple-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                        </motion.div>

                        {/* Caption bar */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80 backdrop-blur-md p-4 lg:p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-10">
                          <div className="flex flex-col gap-3">
                            <div>
                              <h3 className="text-lg lg:text-xl font-bold text-white mb-1">
                                Expert Guidance
                              </h3>
                              <p className="text-xs lg:text-sm text-gray-300">
                                Tailored demonstrations with proven results
                              </p>
                            </div>
                            <motion.div
                              whileHover={{ rotate: 360 }}
                              transition={{ duration: 0.6 }}
                              className="w-10 h-10 bg-gradient-to-r from-purple-500 to-orange-500 rounded-full flex items-center justify-center self-end"
                            >
                              <Users className="w-5 h-5 text-white" />
                            </motion.div>
                          </div>
                        </div>
                      </div>

                      {/* Floating stat badges */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 }}
                        className="absolute -left-2 lg:-left-4 top-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-orange-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
                          Bespoke
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Tailored</div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 }}
                        className="absolute -right-2 lg:-right-4 bottom-1/3 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-purple-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-500 to-pink-600 bg-clip-text text-transparent">
                          Expert-Led
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Guidance</div>
                      </motion.div>
                    </motion.div>
                  </div>

                  {/* Image 3 */}
                  <div className="lg:col-span-1">
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="relative group h-full"
                    >
                      {/* Decorative background elements */}
                      <div className="absolute -inset-4 bg-gradient-to-r from-pink-500/20 via-blue-500/20 to-[#BBD134]/20 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                      {/* Main image container */}
                      <div className="relative glass-morphism-dark rounded-2xl overflow-hidden border-2 border-pink-400/30 hover:border-pink-400/60 transition-all duration-500 shadow-2xl h-full min-h-[350px]">
                        {/* Accent bar with brand color */}
                        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-pink-500 via-blue-500 to-[#BBD134] z-10" />

                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className="relative h-full"
                        >
                          <img
                            src="/images/branded/3.png"
                            alt="Professional Solutions"
                            className="w-full h-full object-cover"
                          />

                          {/* Overlay gradient on hover */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          {/* Interactive corner accents */}
                          <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-pink-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform -translate-x-2 -translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                          <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-pink-400 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-x-2 translate-y-2 group-hover:translate-x-0 group-hover:translate-y-0" />
                        </motion.div>

                        {/* Caption bar */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80 backdrop-blur-md p-4 lg:p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 z-10">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg lg:text-xl font-bold text-white mb-1">
                                Professional Solutions
                              </h3>
                              <p className="text-xs lg:text-sm text-gray-300">
                                Enterprise-grade infrastructure and support
                              </p>
                            </div>
                            <motion.div
                              whileHover={{ rotate: 360 }}
                              transition={{ duration: 0.6 }}
                              className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0"
                            >
                              <Star className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                            </motion.div>
                          </div>
                        </div>
                      </div>

                      {/* Floating stat badges */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 }}
                        className="absolute -left-2 lg:-left-4 top-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-pink-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-pink-500 to-rose-600 bg-clip-text text-transparent">
                          AI-Powered
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Insights</div>
                      </motion.div>

                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.6 }}
                        className="absolute -right-2 lg:-right-4 bottom-1/4 glass-morphism-dark rounded-xl p-3 lg:p-4 border border-blue-400/40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-lg shadow-xl opacity-0 group-hover:opacity-100"
                      >
                        <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-blue-500 to-[#BBD134] bg-clip-text text-transparent">
                          Enterprise
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Grade</div>
                      </motion.div>
                    </motion.div>
                  </div>
                </div>
                </div>
              </div>

              {/* Search and Filter Bar */}
              <div className="container mx-auto px-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="glass-morphism-strong rounded-xl p-6 border border-white/20 dark:border-white/10"
                >
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Search Input */}
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        placeholder="Search demos, technologies, or features..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-white/50 dark:bg-gray-800/50 border-white/30 dark:border-gray-600/30 focus:border-blue-400 dark:focus:border-blue-400"
                      />
                    </div>

                    {/* Filter Button */}
                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="bg-white/50 dark:bg-gray-800/50 border-white/30 dark:border-gray-600/30 hover:bg-white/70 dark:hover:bg-gray-700/50"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      Filters
                      <ChevronDown className={cn(
                        "h-4 w-4 ml-2 transition-transform duration-200",
                        showFilters && "rotate-180"
                      )} />
                    </Button>
                  </div>

                  {/* Filters Panel */}
                  <AnimatePresence>
                    {showFilters && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-4 pt-4 border-t border-white/20 dark:border-white/10"
                      >
                        <div className="flex flex-wrap gap-4">
                          {/* Status Filter */}
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Status
                            </label>
                            <select
                              value={selectedStatus}
                              onChange={(e) => setSelectedStatus(e.target.value)}
                              className="w-full px-3 py-2 bg-white/50 dark:bg-gray-800/50 border border-white/30 dark:border-gray-600/30 rounded-md"
                            >
                              <option value="all">All Statuses</option>
                              <option value="ACTIVE">Active</option>
                              <option value="INACTIVE">Inactive</option>
                              <option value="MAINTENANCE">Maintenance</option>
                            </select>
                          </div>

                          {/* Sort By */}
                          <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Sort By
                            </label>
                            <select
                              value={sortBy}
                              onChange={(e) => setSortBy(e.target.value)}
                              className="w-full px-3 py-2 bg-white/50 dark:bg-gray-800/50 border border-white/30 dark:border-gray-600/30 rounded-md"
                            >
                              <option value="name">Name</option>
                              <option value="duration">Duration</option>
                              <option value="popularity">Popularity</option>
                            </select>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Demo Grid */}
        <section className="pb-20">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" text="Loading demos..." />
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                <AnimatePresence>
                  {filteredDemos.map((demo, index) => (
                    <motion.div
                      key={demo.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                      onClick={() => handleDemoClick(demo.id)}
                      className="glass-morphism-strong rounded-xl p-6 border border-white/20 dark:border-white/10 hover:border-blue-400/50 dark:hover:border-blue-400/50 cursor-pointer group"
                    >
                      {/* Status Badge */}
                      <div className="flex items-center justify-between mb-4">
                        <div className={cn(
                          "px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r",
                          statusColors[demo.status as keyof typeof statusColors]
                        )}>
                          <Activity className="h-3 w-3 mr-1 inline" />
                          {demo.status}
                        </div>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                          <Star className="h-4 w-4 mr-1" />
                          {demo.totalBookings}
                        </div>
                      </div>

                      {/* Demo Title */}
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors min-h-[3.5rem] flex items-start">
                        {demo.name}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">
                        {demo.description}
                      </p>

                      {/* Demo Details */}
                      <div className="flex items-center gap-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {demo.durationMinutes}min
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          Max {demo.maxAttendees}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {demo.tags.slice(0, 3).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-2 py-1 bg-blue-500/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Products */}
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Featured Products:</p>
                        <div className="space-y-1">
                          {demo.products.slice(0, 2).map((product, productIndex) => (
                            <div key={productIndex} className="text-xs text-gray-600 dark:text-gray-300">
                              <span className="font-medium">{product.vendor}</span> {product.productName}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDemoClick(demo.id)
                          }}
                        >
                          <BookOpen className="h-4 w-4 mr-2" />
                          Learn More
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleBookDemo(e, demo.id)}
                          className="bg-white/50 dark:bg-gray-800/50 border-white/30 dark:border-gray-600/30 hover:bg-white/70 dark:hover:bg-gray-700/50"
                        >
                          <Calendar className="h-4 w-4 mr-2" />
                          Book
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}

            {/* No Results */}
            {filteredDemos.length === 0 && !isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="glass-morphism-strong rounded-xl p-12 border border-white/20 dark:border-white/10 max-w-md mx-auto">
                  <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No demos found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Try adjusting your search terms or filters
                  </p>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}